import { useCallback, useState } from "react";
import Section from "../../parts/Section";
import Api, { TechnicalAuthorizationsListItem } from "../../Api";
import EntitySearch from "../../parts/EntitySearch";
import EntityList from "../../parts/EntityList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import BoolTabs from "../../parts/BoolTabs";
import classNames from "classnames";
import DiscreetMessage from "../../parts/DiscreetMessage";
import { Client } from "../../types";
import { useSearchParams } from "react-router-dom";

const TechnicalList = () => {
  const [searchParams] = useSearchParams();
  const showAuthorizations = searchParams.get("tab") === "autorizacoes";

  const [quotes, setQuotes] = useState<
    TechnicalAuthorizationsListItem[] | null
  >(null);
  const [clients, setClients] = useState<Client[] | null>(null);

  const searchFunctionAuthorizations = useCallback(
    (searchTerm: string, page: string) =>
      Api.technicalAuthorizationsList(searchTerm, page),
    []
  );

  const searchFunctionClients = useCallback(
    (searchTerm: string, page: string) =>
      Api.technicalClientsList(searchTerm, page),
    []
  );

  return (
    <>
      <div className="border-b">
        <Section className="p-4">
          {showAuthorizations && (
            <EntitySearch
              searchFunction={searchFunctionAuthorizations}
              setEntities={setQuotes}
            />
          )}
          {!showAuthorizations && (
            <EntitySearch
              searchFunction={searchFunctionClients}
              setEntities={setClients}
            />
          )}
        </Section>
      </div>

      <BoolTabs
        defaultTab={{ label: "Clientes", value: "clientes" }}
        alternateTab={{ label: "Autorizações", value: "autorizacoes" }}
        onChange={() => [setQuotes(null), setClients(null)]}
      />

      <Section>
        {showAuthorizations ? (
          <EntityList entities={quotes} Card={Authorization} />
        ) : (
          <EntityList entities={clients} Card={ClientCard} />
        )}
      </Section>
    </>
  );
};

export default TechnicalList;

const ClientCard = ({
  entity: client,
  expanded,
  setExpanded,
}: {
  entity: Client;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}) => {
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState(client.technicalNotes);
  const [newNote, setNewNote] = useState("");

  const addNote = (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    Api.technicalAddNote(client.uuid, newNote).then((notes) => [
      setNewNote(""),
      setNotes(notes),
      setSaving(false),
    ]);
  };

  const deleteNote = (i: number) => () => {
    setSaving(true);
    if (window.confirm("Apagar anotação?")) {
      Api.technicalDeleteNote(client.uuid, i).then((notes) => [
        setNotes(notes),
        setSaving(false),
      ]);
    }
  };

  return (
    <div className="border rounded-lg shadow-sm">
      <div
        className={classNames("flex hover:bg-slate-50 cursor-pointer", {
          "border-b": expanded,
        })}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 p-4 border-r font-bold whitespace-nowrap overflow-scroll no-scrollbar">
          {client.name}
        </div>
        <div className="p-4 font-bold flex items-center font-mono">
          {notes.length}
          <FontAwesomeIcon
            icon="note-sticky"
            className="text-sm text-gray-400 ml-1"
          />
        </div>
      </div>

      {expanded && (
        <div>
          <div className="p-4 flex flex-col gap-2">
            {notes.length === 0 && <DiscreetMessage text="Nenhuma anotação" />}

            {notes.map((note, i) => (
              <div
                key={i}
                className="border rounded-2xl pt-0.5 pb-1 px-2.5 text-sm leading-snug"
              >
                <div className="text-xs flex text-gray-500 mb-1">
                  <span className="font-bold mr-1">{note.user}</span>
                  <span className="font-light flex-1">
                    {moment(note.date).format("DD/MM/YY")}
                  </span>
                  <button
                    className="px-1 -mr-1"
                    onClick={deleteNote(i)}
                    disabled={saving}
                  >
                    ×
                  </button>
                </div>
                <div>{note.text}</div>
              </div>
            ))}

            <form onSubmit={addNote}>
              <input
                className="border rounded-full w-full text-sm py-1 px-3 disabled:text-gray-500"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                disabled={saving}
              />
            </form>
          </div>

          <div className="flex border-t text-xs">
            <div
              className="border-r rounded-bl-lg p-3 font-bold hover:bg-slate-50 cursor-pointer"
              onClick={() => window.open(`/os/cliente/${client.uuid}`)}
            >
              Criar OS
            </div>

            <button
              className="rounded-br-lg p-3 hover:bg-slate-50 flex-1 text-right"
              onClick={() => setExpanded(false)}
            >
              <FontAwesomeIcon icon="chevron-up" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Authorization = ({
  entity: { quote, clientName },
}: {
  entity: TechnicalAuthorizationsListItem;
}) => {
  return (
    <div
      className="border rounded-lg shadow-sm hover:bg-slate-50 cursor-pointer"
      onClick={() => window.open(`/os/autorizacao/${quote.uuid}`)}
    >
      <div className="font-bold whitespace-nowrap overflow-scroll no-scrollbar py-2 px-4">
        {clientName}
      </div>

      <div className="text-xs font-light border-t px-4 py-1 text-gray-500">
        <FontAwesomeIcon icon="check-circle" className="text-gray-400 mr-3" />
        <span className="font-semibold">Autorizado</span>
        {quote.authorizationDate && (
          <>
            {" "}
            em {moment(quote.authorizationDate).format("DD/MM/YY")} às{" "}
            {moment(quote.authorizationDate).format("HH:mm")}
          </>
        )}
      </div>
    </div>
  );
};
